import { intersection } from "lodash";
import { useMemo } from "react";

import { SessionInferenceMode } from "@/components/graphql/schema.graphql";
import { useAccess, useBasicData } from "@/hooks";
import { getObjectType } from "@/utils/libraryObjectUtils";
import { isSVGMimeType } from "@/utils/mimetype";

import { DriveStyleFragment } from "../../../drive/queries.graphql";
import {
  ActionId,
  DriveFileItem,
  PageContext,
  SimpleFile,
  SimpleFileWithInference,
} from "../../types";

type SelectionMode = "single" | "multiple";

// This is a mapping to remove action ids if the inference mode is not supported (disabled by workspace)
const ActionIdsByInferenceMode: Record<SessionInferenceMode, ActionId[]> = {
  REALTIME: [],
  CREATE: ["VARIATION", "FORGE_WITH_STYLE"],
  CREATE_3_D: ["CREATE_3D"],
  CREATE_VIDEO: ["CREATE_VIDEO"],
  ANIMATE_MESH: [],
  CREATE_SPEECH: [],
  CREATE_SOUND_EFFECT: [],
  BLUEPRINT: [],

  PROMPT_EDIT_IMAGE: ["EDIT"],
  PROMPT_EDIT_VIDEO: ["CREATE_VIDEO"],

  UPSCALE: ["UPSCALE"],
  REFRAME_IMAGE: [],
  VECTORIZE_IMAGE: ["VECTORIZE_IMAGE"],
  REMOVE_BACKGROUND: ["REMOVE_BACKGROUND"],
  SPLIT_IMAGE_INTO_LAYERS: [],

  UPSCALE_VIDEO: ["CREATE_VIDEO"],
  REFRAME_VIDEO: ["CREATE_VIDEO"],
  ADD_SFX_VIDEO: ["CREATE_VIDEO"],
  REMOVE_BACKGROUND_VIDEO: ["CREATE_VIDEO"],

  CANVAS_2_D: ["EDIT"],
  CANVAS_PROMPT_EDIT: ["EDIT"],

  RETEXTURE_MESH: [],
  RIG_MESH: [],
  REMESH: [],
  SEGMENT_MESH: [],
};

const doesContextAllowForDeletion = (pageContext: PageContext) =>
  ["session", "drive", "history"].includes(pageContext);

const getImageFileActions = (
  pageContext: PageContext,
  // Please don't make into an array again. We perform an intersection on all possible actions.
  file: SimpleFile,
  isUserInAllStyleWorkspaces: boolean,
  canEditAllStyles: boolean,
  selectionMode: SelectionMode,
): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
    "EXPORT",
    "REPORT",
    "DOWNLOAD",
    "CREATE_STYLE_FROM_IMAGES",
    "REFINE",
    "UPSCALE",
    "REFRAME_IMAGE",
    "VECTORIZE_IMAGE",
    "REMOVE_BACKGROUND",
    "PROMPT_EDIT_IMAGE",
  ];

  actionIds.push("DOWNLOAD_AS");

  if (selectionMode === "single") {
    actionIds.push("EDIT", "CREATE_3D", "CREATE_VIDEO", "VARIATION");
  }

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  if (file.isGenerated) {
    if (isUserInAllStyleWorkspaces) {
      actionIds.push("POST");
    }

    if (selectionMode === "single") {
      if (!file?.inferenceParameters?.realtime) {
        actionIds.push("REFORGE");
      }

      if (file?.styles?.length > 0 && canEditAllStyles) {
        actionIds.push("SET_AS_COVER_IMAGE");
      }
    }
  }

  return actionIds;
};

const getSVGFileActions = (
  pageContext: PageContext,
  file: SimpleFile,
  selectionMode: SelectionMode,
): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "EXPORT",
    "REPORT",
    "DOWNLOAD",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
  ];

  if (file.isGenerated) {
    if (selectionMode === "single") {
      actionIds.push("REFORGE");
    }
  }

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  return actionIds;
};

const getPSDFileActions = (
  pageContext: PageContext,
  file: SimpleFile,
  selectionMode: SelectionMode,
): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "EXPORT",
    "REPORT",
    "DOWNLOAD",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
  ];

  if (file.isGenerated) {
    if (selectionMode === "single") {
      actionIds.push("REFORGE");
    }
  }
  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  return actionIds;
};

const getMeshFileActions = (
  pageContext: PageContext,
  // Please don't make into an array again. We perform an intersection on all possible actions.
  file: SimpleFile,
  isUserInAllStyleWorkspaces: boolean,
  selectionMode: SelectionMode,
): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "EXPORT",
    "REPORT",
    "DOWNLOAD",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
  ];

  if (selectionMode === "single") {
    actionIds.push("RETEXTURE_MESH");
    actionIds.push("RIG_MESH");
    actionIds.push("REMESH");
  }

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  if (file.isGenerated) {
    if (selectionMode === "single") {
      actionIds.push("REFORGE");
    }

    if (isUserInAllStyleWorkspaces) {
      actionIds.push("POST");
    }
  }

  return actionIds;
};

const getVideoFileActions = (
  pageContext: PageContext,
  file: SimpleFile, // Please don't make into an array again. We perform an intersection on all possible actions.
  isUserInAllStyleWorkspaces: boolean,
  selectionMode: SelectionMode,
): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "DOWNLOAD",
    "EXPORT",
    "REPORT",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
  ];

  if (selectionMode === "single") {
    actionIds.push(
      "REFINE",
      "UPSCALE_VIDEO",
      "REFRAME_VIDEO",
      "ADD_SFX_VIDEO",
      "REMOVE_BACKGROUND_VIDEO",
      "PROMPT_EDIT_VIDEO",
    );
  }

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  if (file.isGenerated) {
    if (selectionMode === "single") {
      actionIds.push("REFORGE");
    }

    if (isUserInAllStyleWorkspaces) {
      actionIds.push("POST");
    }
  }

  return actionIds;
};

const getAudioFileActions = (
  pageContext: PageContext,
  file: SimpleFile,
  isUserInAllStyleWorkspaces: boolean,
  selectionMode: SelectionMode,
): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "EXPORT",
    "REPORT",
    "DOWNLOAD",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
  ];

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  if (file.isGenerated && isUserInAllStyleWorkspaces) {
    actionIds.push("POST");
  }

  if (file.isGenerated && selectionMode === "single") {
    actionIds.push("REFORGE");
  }

  return actionIds;
};

const getModelFileActions = (
  pageContext: PageContext,
  _: SimpleFile,
  selectionMode: SelectionMode,
): ActionId[] => {
  if (selectionMode === "multiple") {
    return getGeneralFileActions(pageContext);
  }

  const actionIds: ActionId[] = [
    "TRAIN",
    "LIKE",
    "CREATE_STYLE_FROM_MODEL",
    "DOWNLOAD",
  ];

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  return actionIds;
};

const getAssetActions = (
  pageContext: PageContext,
  selectionMode: SelectionMode,
): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
    "EXPORT",
    "REFINE",
    "UPSCALE",
    "REFRAME_IMAGE",
    "VECTORIZE_IMAGE",
    "REMOVE_BACKGROUND",
    "PROMPT_EDIT_IMAGE",
  ];

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  if (selectionMode === "single") {
    actionIds.push("EDIT", "VARIATION", "CREATE_VIDEO", "CREATE_3D");
  }

  return actionIds;
};

const getFailedInferenceActions = (): ActionId[] => {
  return ["REFORGE"];
};

const getDirectoryActions = (
  pageContext: PageContext,
  selectionMode: SelectionMode,
) => {
  const actionIds: ActionId[] = [];

  if (selectionMode === "single") {
    actionIds.push("RENAME_DIRECTORY");
  }

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  return actionIds;
};

const getStyleActions = (
  pageContext: PageContext,
  style: DriveStyleFragment,
  selectionMode: SelectionMode,
) => {
  const actionIds: ActionId[] = [];

  if (doesContextAllowForDeletion(pageContext)) {
    actionIds.push("DELETE");
  }

  if (selectionMode === "single" && style.styleStatus === "COMPLETE") {
    actionIds.push("DUPLICATE_STYLE", "FORGE_WITH_STYLE");
  }

  return actionIds;
};

const getGeneralFileActions = (context: PageContext): ActionId[] => {
  const actionIds: ActionId[] = [
    "LIKE",
    "EXPORT",
    "DOWNLOAD",
    "COPY_TO_CLIPBOARD",
    "COPY_LINK",
  ];

  if (doesContextAllowForDeletion(context)) {
    actionIds.push("DELETE");
  }

  return actionIds;
};

interface UseAvailableActionIdsProps {
  objects: DriveFileItem[];
  pageContext: PageContext;
}

export const useAvailableActionIds = (
  props: UseAvailableActionIdsProps,
): Set<ActionId> => {
  const { objects, pageContext } = props;

  const { hasPermissionInWorkspace, hasInferenceMode } = useAccess();
  const { userWorkspaces } = useBasicData();

  const userMembershipIds = userWorkspaces?.map((workspace) => workspace.id);

  const isUserInAllStylesWorkspaces = useMemo(
    () =>
      objects.every((item) => {
        const simpleFile = item as SimpleFile;
        return (
          simpleFile?.styles?.length > 0 &&
          simpleFile.styles.every((style) =>
            userMembershipIds.includes(style.workspace?.id),
          )
        );
      }),
    [objects, userMembershipIds],
  );

  const canEditAllStyles = useMemo(() => {
    if (!isUserInAllStylesWorkspaces) {
      return false;
    }

    return objects.every((item) =>
      (item as SimpleFile)?.styles?.every((style) =>
        hasPermissionInWorkspace("style:edit", style.workspace?.id),
      ),
    );
  }, [objects, isUserInAllStylesWorkspaces, hasPermissionInWorkspace]);

  return useMemo(() => {
    const selectionMode: SelectionMode =
      objects.length > 1 ? "multiple" : "single";

    // To evaluate the available actions, we get all the possible actions for each selected
    // item, then we take the intersection of all possible actions. This avoids us having to
    // maintain a complex matrix of possibilities across selections.
    const availableActionIds = objects.reduce<ActionId[]>(
      (possibleActions, item, i) => {
        let itemActions: ActionId[] = getGeneralFileActions(pageContext);

        const itemType = item?.__typename;
        const simpleFile = item as SimpleFileWithInference;

        if (itemType === "File") {
          const fileType = getObjectType(simpleFile);
          switch (fileType) {
            case "image":
              {
                const isSVG = isSVGMimeType(item.contentType);

                if (isSVG) {
                  itemActions = getSVGFileActions(
                    pageContext,
                    item as SimpleFile,
                    selectionMode,
                  );
                } else {
                  itemActions = getImageFileActions(
                    pageContext,
                    item as SimpleFile,
                    isUserInAllStylesWorkspaces,
                    canEditAllStyles,
                    selectionMode,
                  );
                }
              }
              break;
            case "psd":
              {
                itemActions = getPSDFileActions(
                  pageContext,
                  item as SimpleFile,
                  selectionMode,
                );
              }
              break;
            case "mesh":
              itemActions = getMeshFileActions(
                pageContext,
                simpleFile,
                isUserInAllStylesWorkspaces,
                selectionMode,
              );
              break;
            case "video":
              itemActions = getVideoFileActions(
                pageContext,
                simpleFile,
                isUserInAllStylesWorkspaces,
                selectionMode,
              );
              break;
            case "audio":
              itemActions = getAudioFileActions(
                pageContext,
                simpleFile,
                isUserInAllStylesWorkspaces,
                selectionMode,
              );
              break;
            case "safetensors":
              itemActions = getModelFileActions(
                pageContext,
                simpleFile,
                selectionMode,
              );
              break;
          }
        } else if (itemType === "Asset") {
          itemActions = getAssetActions(pageContext, selectionMode);
        } else if (itemType === "Directory") {
          itemActions = getDirectoryActions(pageContext, selectionMode);
        } else if (itemType === "Style") {
          itemActions = getStyleActions(pageContext, item, selectionMode);
        }

        if (simpleFile.inference?.status === "FAILED") {
          itemActions = getFailedInferenceActions();
        }

        if (i === 0) {
          return itemActions;
        }

        // We only return the actions that are supported by all items.
        return intersection(possibleActions, itemActions);
      },
      [],
    );

    const actionIds = new Set(availableActionIds);

    // Remove actions that are not supported by the inference mode (disabled by workspace)
    for (const inferenceMode of Object.keys(ActionIdsByInferenceMode)) {
      const mode = inferenceMode as SessionInferenceMode;
      if (!hasInferenceMode(mode)) {
        const actionsToDelete =
          ActionIdsByInferenceMode[inferenceMode as SessionInferenceMode];
        actionsToDelete.forEach((actionId) => actionIds.delete(actionId));
      }
    }

    return actionIds;
  }, [
    objects,
    pageContext,
    isUserInAllStylesWorkspaces,
    canEditAllStyles,
    hasInferenceMode,
  ]);
};
